/**
 * Formatting for engineering numbers.
 *
 * Kept apart from the panels that use it so the rules are stated once and are
 * testable without a DOM: a cell count and a quality metric are read very
 * differently, and a single `toLocaleString` everywhere gets one of them wrong.
 */

/** 13877 -> "13,877". Counts are read in groups; nothing is rounded away. */
export function count(value) {
  if (!Number.isFinite(value)) return '—';
  return Math.round(value).toLocaleString('en-US');
}

/**
 * A measured quantity, at a precision that does not imply more than was
 * measured. Quality metrics live in [0, 1] and are compared at three decimals;
 * lengths span many orders of magnitude, so very small and very large values
 * go to exponential rather than to a row of zeros.
 */
export function metric(value, digits = 3) {
  if (!Number.isFinite(value)) return '—';
  if (value !== 0 && (Math.abs(value) < 1e-3 || Math.abs(value) >= 1e6)) {
    return value.toExponential(2);
  }
  return value.toFixed(digits);
}

/** A length with its model unit, e.g. "2.00 mm". The unit is never assumed. */
export function length(value, unit) {
  if (!Number.isFinite(value)) return '—';
  return `${metric(value, 2)}${unit ? ` ${unit}` : ''}`;
}

/** Bytes, for artifact sizes. Binary units, because the file system's are. */
export function bytes(value) {
  if (!Number.isFinite(value)) return '—';
  const units = ['B', 'KiB', 'MiB', 'GiB'];
  let size = value;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  return `${index === 0 ? size : size.toFixed(1)} ${units[index]}`;
}

/**
 * A content address, shortened for display only.
 *
 * "sha256:1a2b3c4d…" -- the algorithm stays, because a digest with its
 * algorithm stripped is a number of unknown provenance, and the full value is
 * always available from the record itself.
 */
export function digest(value, keep = 10) {
  if (typeof value !== 'string' || value === '') return '—';
  const [algorithm, hex] = value.split(':');
  if (!hex) return value.slice(0, keep);
  return `${algorithm}:${hex.slice(0, keep)}…`;
}

/** An ISO timestamp as a short local one. Engineering history is read in order. */
export function when(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "GENERATION 05" -- a stable width for the history rail up to 99. */
export function generationLabel(ordinal) {
  if (!Number.isFinite(ordinal)) return '—';
  return String(ordinal).padStart(2, '0');
}

/** A role or status slug as a display word: "boundary_layer" -> "Boundary layer". */
export function slug(value) {
  if (typeof value !== 'string' || value === '') return '—';
  const spaced = value.replace(/[_-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
